import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  onSuccess: () => void;
  onError: (message: string) => void;
}

// ─── Native Android Variante ─────────────────────────────────────────────────
function NativeGoogleButton({ onSuccess, onError }: Props) {
  const { loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleNativeLogin = async () => {
    setLoading(true);
    try {
      const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

      await GoogleAuth.initialize({
        clientId: '864696696898-ih7847iu0qa7ga9hst0tmsf5ksttamvn.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });

      const googleUser = await GoogleAuth.signIn();
      const idToken = googleUser?.authentication?.idToken;

      if (!idToken) {
        throw new Error('Kein ID-Token von Google erhalten');
      }

      await loginWithGoogle(idToken);
      onSuccess();
    } catch (err: any) {
      if (err?.message?.includes('cancel') || err?.message?.includes('12501')) {
        // User hat Auswahl abgebrochen – kein Fehler anzeigen
        return;
      }
      const msg = err?.response?.data?.message ?? err?.message ?? 'Google-Anmeldung fehlgeschlagen';
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleNativeLogin}
      disabled={loading}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        border: '1.5px solid var(--gray-300)',
        borderRadius: '0.5rem',
        background: 'var(--bg-primary)',
        color: 'var(--gray-800)',
        fontSize: '0.9375rem',
        fontWeight: '500',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      {/* Google Logo */}
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
      </svg>
      {loading ? 'Wird angemeldet…' : 'Mit Google anmelden'}
    </button>
  );
}

// ─── Web Variante (nutzt @react-oauth/google) ─────────────────────────────────
function WebGoogleButton({ onSuccess, onError }: Props) {
  const { loginWithGoogle } = useAuth();

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <GoogleLogin
        onSuccess={async (credentialResponse) => {
          try {
            const idToken = credentialResponse.credential;
            if (!idToken) throw new Error('Kein ID-Token erhalten');
            await loginWithGoogle(idToken);
            onSuccess();
          } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Google-Anmeldung fehlgeschlagen';
            onError(msg);
          }
        }}
        onError={() => onError('Google-Anmeldung fehlgeschlagen')}
        theme="outline"
        size="large"
        width="360"
        text="continue_with"
        shape="rectangular"
        locale="de"
      />
    </div>
  );
}

// ─── Haupt-Export: wählt automatisch die richtige Variante ───────────────────
export default function GoogleSignInButton(props: Props) {
  if (Capacitor.isNativePlatform()) {
    return <NativeGoogleButton {...props} />;
  }
  return <WebGoogleButton {...props} />;
}
